import { fetchEventSource } from '@microsoft/fetch-event-source';
import { store } from '../store/store';
import { ENDPOINTS } from './endpoints';
import api from './axios/api-service';
import { getPersistedAccessToken } from '../shared/authSession';

const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 20000;

class AuthError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
  }
}

const getApiBaseUrl = () => (
  api?.defaults?.baseURL
  || process.env.REACT_APP_API_BASE_URL
  || `${window.location.protocol}//${window.location.host}/api/v1`
);

const getAccessToken = () => {
  const token = store.getState()?.auth?.accessToken;
  if (token) return token;
  return getPersistedAccessToken();
};

const buildHeaders = () => {
  const headers = {
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache'
  };
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const extractNotificationData = (data) => {
  if (data == null || data === '') return null;
  
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed === '') return null;
    try {
      const parsed = JSON.parse(trimmed);
      return extractNotificationData(parsed);
    } catch (error) {
      return null;
    }
  }
  
  if (typeof data === 'object') {
    const rawUnreadCount = data.unread_count ?? data.unreadCount ?? data.unread ?? data.count;
    const rawNewNotificationCount = data.new_notification_count ?? data.newNotificationCount ?? 0;
    const projectId =
      data.project_id
      ?? data.projectId
      ?? data.project?.id
      ?? data.project?.project_id
      ?? data.notification?.project_id
      ?? data.notification?.projectId
      ?? null;
    const unreadCount = Number(rawUnreadCount);
    const newNotificationCount = Number(rawNewNotificationCount);

    if (Number.isFinite(unreadCount)) {
      return {
        unreadCount,
        newNotificationCount: Number.isFinite(newNotificationCount) ? newNotificationCount : 0,
        projectId
      };
    }
  }
  
  return null;
};

export const startNotificationStream = ({
  onUnreadCount,
  onAiResponseCompleted,
  onError,
  onOpen,
  onAuthRejected
} = {}) => {
  let controller = null;
  let authRejected = false;
  let retryAttempt = 0;
  let reconnectTimeoutId = null;
  let stopped = false;
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${ENDPOINTS.NOTIFICATIONS_STREAM}`;

  const resetRetryState = () => {
    retryAttempt = 0;
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
  };

  const getRetryDelay = () => {
    const delay = Math.min(INITIAL_RETRY_DELAY_MS * (2 ** retryAttempt), MAX_RETRY_DELAY_MS);
    retryAttempt += 1;
    return delay;
  };

  const scheduleReconnect = () => {
    if (stopped || authRejected || reconnectTimeoutId) return;
    reconnectTimeoutId = setTimeout(() => {
      reconnectTimeoutId = null;
      connect();
    }, getRetryDelay());
  };

  const connect = () => {
    if (stopped || authRejected) return;
    controller = new AbortController();

    fetchEventSource(url, {
      method: 'GET',
      headers: buildHeaders(),
      signal: controller.signal,
      openWhenHidden: true,
      onopen: (response) => {
        if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
          resetRetryState();
          onOpen?.(response);
          return;
        }

        if (response.status === 401 || response.status === 403) {
          authRejected = true;
          const error = new AuthError(`Notification stream rejected with status ${response.status}`, response.status);
          console.error('❌ Notification stream authentication failed:', {
            status: response.status,
            message: error.message
          });
          onAuthRejected?.(error, response);
          onError?.(error);
          controller.abort();
          return;
        }

        const error = new Error(`Notification stream failed with status ${response.status}`);
        console.error('❌ Notification stream connection failed:', {
          status: response.status,
          statusText: response.statusText,
          url
        });
        throw error;
      },
      onmessage: (message) => {
        let parsedData = null;
        if (message?.data) {
          try {
            parsedData = JSON.parse(message.data);
          } catch (error) {
            parsedData = null;
          }
        }

        if (
          message?.event === 'ai_response_completed'
          || parsedData?.type === 'AI_RESPONSE_COMPLETED'
        ) {
          onAiResponseCompleted?.(parsedData, message);
        }

        const notificationData = extractNotificationData(message?.data);
        if (notificationData) {
          onUnreadCount?.(notificationData.unreadCount, notificationData.newNotificationCount, notificationData, message);
        }
      },
      onclose: () => {
        if (controller.signal.aborted || authRejected || stopped) return;
        console.error('❌ Notification stream closed unexpectedly');
        throw new Error('Notification stream closed unexpectedly');
      },
      onerror: (error) => {
        throw error;
      }
    }).catch((error) => {
      if (stopped || authRejected || controller?.signal.aborted) return;
      console.error('❌ Notification stream error:', {
        message: error.message,
        name: error.name,
        retryAttempt,
        nextRetryDelay: `${getRetryDelay()}ms`
      });
      onError?.(error);
      scheduleReconnect();
    });
  };

  connect();

  return () => {
    stopped = true;
    authRejected = false;
    resetRetryState();
    controller?.abort();
  };
};
